/* ==========================================================================
   ODYSSÉE ACADEMY : panier flottant (drawer)
   Contrat public : window.OdaCart = { open, close, refresh, addFromForm, add }
   Aucune dépendance. Écoute déléguée sur un conteneur stable (#cart-drawer)
   pour survivre au remplacement du contenu après chaque mutation du panier
   et au rechargement de section dans l’éditeur de thème.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var GENERIC_ERROR = 'Une erreur est survenue. Veuillez réessayer.';
  var DEBOUNCE_MS = 300;

  var root = null;           // #cart-drawer, réassigné à chaque (re)liaison
  var lastFocused = null;    // élément à refocaliser à la fermeture
  var busy = false;          // une requête de mutation est en cours
  var pendingQty = {};       // ligne -> quantité en attente (débounce +/-)
  var pendingTimers = {};    // ligne -> identifiant de timeout

  /* --- Utilitaires ---------------------------------------------------------- */

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function getInner() { return root ? qs('.cart-drawer-inner', root) : null; }

  function setBusy(state) {
    busy = state;
    if (root) root.classList.toggle('is-busy', state);
  }

  function showError(message) {
    var err = root ? qs('[data-cart-error]', root) : null;
    if (!err) return;
    if (!message) {
      err.hidden = true;
      err.textContent = '';
      return;
    }
    err.textContent = message;
    err.hidden = false;
  }

  function updateBadges(count) {
    qsa('.nav-cart-count').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-hidden', count === 0);
    });
  }

  function extractErrorMessage(data) {
    if (!data) return GENERIC_ERROR;
    return data.description || data.message || GENERIC_ERROR;
  }

  /* --- Ouverture / fermeture ------------------------------------------------- */

  function open() {
    if (!root) return;
    lastFocused = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    root.removeAttribute('inert');
    document.documentElement.classList.add('cart-open');
    var closeBtn = qs('[data-cart-close-btn]', root);
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('inert', '');
    document.documentElement.classList.remove('cart-open');
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  /* --- Re-rendu via la Section Rendering API --------------------------------- */

  function refresh() {
    var url = window.location.pathname + '?section_id=' + SECTION_ID;
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var temp = document.createElement('div');
        temp.innerHTML = html;
        var newInner = temp.querySelector('.cart-drawer-inner');
        var oldInner = getInner();
        if (!newInner || !oldInner) return;
        oldInner.innerHTML = newInner.innerHTML;
        oldInner.setAttribute('data-cart-count', newInner.getAttribute('data-cart-count') || '0');
        updateBadges(parseInt(newInner.getAttribute('data-cart-count'), 10) || 0);
        /* Une suppression décale les numéros de ligne : toute quantité mémorisée
           pour une ligne dont la requête est déjà partie ne décrit plus le même
           article. On purge, en gardant les lignes encore en attente de débounce. */
        Object.keys(pendingQty).forEach(function (line) {
          if (!pendingTimers[line]) delete pendingQty[line];
        });
      });
  }

  /* --- Mutations : quantité et suppression ------------------------------------ */

  function commitChange(line, quantity) {
    setBusy(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: parseInt(line, 10), quantity: quantity })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) showError(extractErrorMessage(result.data));
        return refresh();
      }, function () {
        showError(GENERIC_ERROR);
      })
      .then(
        function () { setBusy(false); },
        function () { setBusy(false); } /* même si le re-rendu échoue, on redonne la main */
      );
  }

  /* Débounce léger : plusieurs clics rapprochés sur + / − du même ligne se
     regroupent en une seule requête reflétant la quantité finale. */
  function scheduleChange(line, quantity) {
    quantity = Math.max(0, quantity);
    pendingQty[line] = quantity;

    var valueEl = root && qs('[data-quantity-box][data-line="' + line + '"] [data-qty-value]', root);
    if (valueEl) valueEl.textContent = quantity;

    if (pendingTimers[line]) clearTimeout(pendingTimers[line]);
    pendingTimers[line] = setTimeout(function () {
      delete pendingTimers[line];
      /* On consomme la valeur : elle ne doit pas servir de point de départ à
         un clic suivant, les numéros de ligne pouvant avoir été renumérotés. */
      var target = pendingQty[line];
      delete pendingQty[line];
      if (typeof target !== 'number') return;
      commitChange(line, target);
    }, DEBOUNCE_MS);
  }

  /* --- Ajout au panier -------------------------------------------------------- */

  function addFromForm(form) {
    if (!(form instanceof HTMLFormElement)) {
      return Promise.reject(new Error('OdaCart.addFromForm attend un élément <form>.'));
    }
    return postAdd(new FormData(form));
  }

  function add(items) {
    var list = Array.isArray(items) ? items : [items];
    return postAdd(JSON.stringify({ items: list }), true);
  }

  function postAdd(body, isJson) {
    setBusy(true);
    showError('');

    var options = {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: body
    };
    if (isJson) options.headers['Content-Type'] = 'application/json';

    return fetch('/cart/add.js', options)
      .then(
        function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        },
        function () {
          /* Échec réseau : pas de réponse exploitable. */
          showError(GENERIC_ERROR);
          throw new Error(GENERIC_ERROR);
        }
      )
      .then(function (result) {
        if (!result.ok) {
          showError(extractErrorMessage(result.data));
          return refresh().then(function () {
            open();
            throw result.data;
          });
        }
        return refresh().then(function () {
          open();
          return result.data;
        });
      })
      .then(
        function (data) { setBusy(false); return data; },
        function (err) { setBusy(false); throw err; }
      );
  }

  /* --- Liaison des écouteurs --------------------------------------------------- */

  function onRootClick(e) {
    var closeTrigger = e.target.closest('[data-cart-close]');
    if (closeTrigger) { close(); return; }

    if (busy) return;

    var qtyBtn = e.target.closest('[data-qty-plus], [data-qty-minus]');
    if (qtyBtn) {
      var box = qtyBtn.closest('[data-quantity-box]');
      if (!box) return;
      var line = box.getAttribute('data-line');
      var valueEl = qs('[data-qty-value]', box);
      var current = parseInt(pendingQty[line], 10);
      if (isNaN(current)) current = parseInt(valueEl ? valueEl.textContent : '0', 10) || 0;
      var next = qtyBtn.hasAttribute('data-qty-plus') ? current + 1 : current - 1;
      scheduleChange(line, next);
      return;
    }

    var removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      var removeLine = removeBtn.getAttribute('data-line');
      if (pendingTimers[removeLine]) { clearTimeout(pendingTimers[removeLine]); delete pendingTimers[removeLine]; }
      commitChange(removeLine, 0);
    }
  }

  /* Les div[role=button] du sélecteur de quantité doivent répondre à Entrée
     et Espace, comme le burger de theme.js. */
  function onRootKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var qtyBtn = e.target.closest('[data-qty-plus], [data-qty-minus]');
    if (!qtyBtn) return;
    e.preventDefault();
    qtyBtn.click();
  }

  function bindRoot() {
    var node = document.getElementById('cart-drawer');
    if (!node) { root = null; return; }
    root = node;
    root.addEventListener('click', onRootClick);
    root.addEventListener('keydown', onRootKeydown);
    if (!root.classList.contains('is-open')) root.setAttribute('inert', '');
  }

  /* Le déclencheur (icône panier de la navbar) vit hors du drawer : écoute
     posée une fois sur document, jamais invalidée par un re-rendu. */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-cart-toggle]');
    if (!toggle) return;
    e.preventDefault();
    open();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root && root.classList.contains('is-open')) close();
  });

  function init() { bindRoot(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* L’éditeur de thème remplace #cart-drawer par un nouveau nœud à chaque
     modification de réglage : on doit ré-acquérir la référence. */
  document.addEventListener('shopify:section:load', init);

  window.OdaCart = {
    open: open,
    close: close,
    refresh: refresh,
    addFromForm: addFromForm,
    add: add
  };
})();
